// Ad attribution + conversion event helpers.
// First-touch wins: once an attribution snapshot is stored, later visits
// never overwrite it (the click that brought them here gets the credit).
// Server-side conversion_events is the source of truth; trackEvent() only
// feeds an on-page tag manager (GTM/LinkedIn/Google) when one is installed.

import { getLandingExperimentContext } from './experiments';

const ATTRIB_KEY = 'glowup_attrib';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'];

const PRIVATE_PROP_KEY = /(^|_)(email|name|caption|reason|message|detail|text|password|token|secret|authorization|prompt|body)($|_)/i;
const API_ERROR_DEDUPE_MS = 30_000;
const recentApiErrors = new Map();
const PRIORITY_RECORDING_EVENTS = new Set([
  'signup_completed',
  'checkout_started',
  'checkout_completed',
  'onboarding_started',
  'onboarding_completed',
]);

const normalizeEventName = (event) => String(event || 'unknown_event')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 120) || 'unknown_event';

// Short, pseudonymous refs make a broken session traceable without sending
// workspace names, user details, captions, or feedback into analytics.
export const safeEntityRef = (value) => {
  const normalized = String(value || '').trim();
  return normalized ? normalized.slice(0, 8) : 'unknown';
};

export const productEventContext = ({
  workspaceId,
  workspaceType,
  contentId,
  contentKind = 'reel_or_post',
  surface = 'unknown',
} = {}) => ({
  workspace_ref: safeEntityRef(workspaceId),
  workspace_type: String(workspaceType || 'unknown').slice(0, 40),
  content_ref: safeEntityRef(contentId),
  content_kind: String(contentKind || 'reel_or_post').slice(0, 40),
  surface: String(surface || 'unknown').slice(0, 60),
});

export const sanitizeAnalyticsPath = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'unknown';

  const path = raw
    .replace(/^https?:\/\/[^/]+/i, '')
    .split(/[?#]/, 1)[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':id')
    .replace(/\b[0-9a-f]{24}\b/gi, ':id')
    .replace(/\/[A-Za-z0-9_-]{32,}(?=\/|$)/g, '/:id');

  return (path || 'unknown').slice(0, 160);
};

export const safeAnalyticsProps = (props = {}) => Object.entries(props).reduce((safe, [key, value]) => {
  const normalizedKey = String(key || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .slice(0, 120);
  if (!normalizedKey || PRIVATE_PROP_KEY.test(normalizedKey)) return safe;

  if (typeof value === 'string') {
    safe[normalizedKey] = value.slice(0, 255);
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    safe[normalizedKey] = value;
  } else if (typeof value === 'boolean') {
    safe[normalizedKey] = value;
  }
  return safe;
}, {});

export const captureAttribution = () => {
  try {
    if (localStorage.getItem(ATTRIB_KEY)) return; // first touch already stored
    const params = new URLSearchParams(window.location.search);
    const attrib = { ...getLandingExperimentContext() };
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) attrib[k] = v.slice(0, 200);
    });
    if (document.referrer) attrib.referrer = document.referrer.slice(0, 200);
    if (Object.keys(attrib).length === 0) return; // organic direct: store nothing
    attrib.landing_path = window.location.pathname.slice(0, 200);
    attrib.landing_host = window.location.host.slice(0, 120);
    localStorage.setItem(ATTRIB_KEY, JSON.stringify(attrib));
  } catch {
    // storage blocked (private mode): tracking is never worth breaking the page
  }
};

export const getAttribution = () => {
  try {
    const raw = localStorage.getItem(ATTRIB_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Pushes to PostHog, dataLayer, and Clarity. All are safe no-ops until installed.
export const trackEvent = (event, props = {}) => {
  const safeEvent = normalizeEventName(event);
  const safeProps = safeAnalyticsProps({ ...getLandingExperimentContext(), ...props });
  try {
    if (typeof window.posthog?.capture === 'function') {
      window.posthog.capture(safeEvent, safeProps);
    }
  } catch {
    // analytics must never block the product action
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: safeEvent, ...safeProps });
  } catch {
    // never let a tag error surface to the user
  }
  try {
    if (typeof window.clarity !== 'function') return;
    Object.entries(safeProps).forEach(([key, value]) => {
      window.clarity('set', key, String(value).slice(0, 255));
    });
    window.clarity('event', safeEvent);
    if (PRIORITY_RECORDING_EVENTS.has(safeEvent)) {
      window.clarity('upgrade', safeEvent);
    }
  } catch {
    // analytics must never block the product action
  }
};

// Records one useful error signal per endpoint/status every 30 seconds. This
// keeps polling failures from flooding Clarity while preserving the first hit.
export const trackApiError = (error, now = Date.now()) => {
  if (!error || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return false;

  const endpoint = sanitizeAnalyticsPath(error.config?.url);
  const method = String(error.config?.method || 'unknown').toUpperCase().slice(0, 12);
  const status = error.response?.status || 'network';
  const errorType = error.response ? 'http' : 'network';
  const signature = `${method}|${endpoint}|${status}`;
  const lastSeen = recentApiErrors.get(signature);

  if (lastSeen !== undefined && now - lastSeen < API_ERROR_DEDUPE_MS) return false;

  recentApiErrors.set(signature, now);
  recentApiErrors.forEach((seenAt, key) => {
    if (now - seenAt >= API_ERROR_DEDUPE_MS) recentApiErrors.delete(key);
  });

  trackEvent('api_error', {
    endpoint,
    method,
    status,
    error_type: errorType,
  });
  return true;
};
